import { formatISO } from 'date-fns'
import { Factory } from 'fishery'
import { v4 as uuidV4 } from 'uuid'
import { OpalApiBFFComponent } from '@opallabs/api'
import { ISO8601Duration, prePlanningBlockKey } from '@opallabs/concepts'
import { formatToDateString, nowISOString } from '../../util/datestring'

type BlockById = OpalApiBFFComponent<'timeline_block'>

export const blockByIdFactory: Factory<BlockById> = Factory.define<BlockById>(
  ({ sequence, params }) => {
    const id = uuidV4()
    const { duration, start_at, end_at } =
      params.category_id === prePlanningBlockKey
        ? {
            duration: null,
            start_at: null,
            end_at: null,
          }
        : {
            duration: 'P30D',
            start_at: formatISO(new Date(), { representation: 'date' }),
            end_at: formatISO(ISO8601Duration.add(new Date(), 'P30D'), {
              representation: 'date',
            }),
          }

    return {
      id,
      category_id: uuidV4(),
      category_type: null,
      child_plan_id: null,
      color: null,
      content: null,
      created_at: nowISOString(),
      description: null,
      duration: duration,
      start_at: start_at,
      end_date: end_at,
      image_id: null,
      is_template: false,
      is_workspace_wide_template: false,
      kind: 'block',
      owner_id: uuidV4(),
      paired_moment_board_object_ids: [],
      paired_moment_id: null,
      row_index: 1,
      title: `Mock Block ${sequence} ${id.substring(0, 5)}`,
      template_description: null,
      template_name: null,
      y_scale: 1,
      connectors: [],
    } satisfies BlockById
  }
)

type BlocksById = OpalApiBFFComponent<'blocks_by_category'>['by_id']

type BlocksIds = Array<{
  blockId: string
  categoryId: string | null
  rowIndex: number
}>

export const composeBlocksById = (blockIds: BlocksIds) => {
  return blockIds.reduce((accum, { blockId, categoryId, rowIndex }) => {
    accum[blockId] = blockByIdFactory.build({
      id: blockId,
      category_id: categoryId,
      row_index: rowIndex,
    })

    return accum
  }, {} as BlocksById)
}

type BlocksByCategory =
  OpalApiBFFComponent<'blocks_by_category'>['by_category_with_position']

export const composeBlocksByCategory = (
  blockIds: string[][],
  categoryIds: Array<string | null>
) => {
  const swimlaneCategoryIds = categoryIds.toSpliced(0, 1) // first entry is parent category
  return swimlaneCategoryIds.reduce((accum, categoryId, i) => {
    accum[categoryId ?? prePlanningBlockKey] = blockIds[i].map((id, i) =>
      blockByCategoryWithPosition.build({ id, row_index: i + 1 })
    )
    return accum
  }, {} as BlocksByCategory)
}

type BlockByCategory = OpalApiBFFComponent<'swimlane_position'>

export const blockByCategoryWithPosition: Factory<BlockByCategory> =
  Factory.define<BlockByCategory>(() => {
    return {
      id: uuidV4(),
      start_at: formatToDateString(new Date()),
      duration: 'P30D',
      end_at: formatISO(ISO8601Duration.add(new Date(), 'P30D'), {
        representation: 'date',
      }),
      row_index: 1,
    } satisfies BlockByCategory
  })
